import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { createStandaloneInvoice, type StandaloneLineInput } from '../../lib/invoices';
import { formatCurrency } from '../../lib/format';

interface NewInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Na succesvol aanmaken — invoiceId van de nieuwe losse factuur. */
  onCreated: (invoiceId: string) => void;
  /** Vooraf gekozen klant (bv. vanaf de klantpagina) — slaat de zoekstap over. */
  preset?: { accountId: string; name: string; isBusiness: boolean } | null;
}

interface CustomerSearchResult {
  id: string;
  type: 'account_person' | 'account_business' | 'lead';
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  blacklisted?: boolean;
  blacklistReden?: string | null;
}

interface ContactRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
}

interface LineRow {
  omschrijving: string;
  aantal: string;
  prijs: string; // per stuk, excl. btw — komma toegestaan
  btwPct: 0 | 9 | 21;
}

function emptyLine(): LineRow {
  return { omschrijving: '', aantal: '1', prijs: '', btwPct: 21 };
}

function parseNum(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function lineExcl(row: LineRow): number {
  const aantal = Math.max(1, Math.round(parseNum(row.aantal)) || 1);
  return Math.round(aantal * parseNum(row.prijs) * 100) / 100;
}

export function NewInvoiceDialog({ isOpen, onClose, onCreated, preset }: NewInvoiceDialogProps) {
  // Klant
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [pickedAccountId, setPickedAccountId] = useState<string | null>(null);
  const [pickedAccountName, setPickedAccountName] = useState('');
  const [pickedIsBusiness, setPickedIsBusiness] = useState(false);
  const [pickedBlacklist, setPickedBlacklist] = useState<{ reden: string | null } | null>(null);
  const [accountContacts, setAccountContacts] = useState<ContactRow[] | null>(null);
  const [defaultContactId, setDefaultContactId] = useState<string | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [pickedContactId, setPickedContactId] = useState<string | null>(null);

  // Merk-branding (PDF + e-mail). Losse facturen default Cinco, maar per stuk te kiezen.
  const [brand, setBrand] = useState<'CINCO' | 'KLEDA'>('CINCO');

  // Regels
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [opmerking, setOpmerking] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setSearchResults([]);
    setSearching(false);
    setPickedAccountId(null);
    setPickedAccountName('');
    setPickedIsBusiness(false);
    setPickedBlacklist(null);
    setAccountContacts(null);
    setDefaultContactId(null);
    setLoadingContacts(false);
    setPickedContactId(null);
    setBrand('CINCO');
    setLines([emptyLine()]);
    setOpmerking('');
    setSubmitting(false);
    setError(null);

    // Vooraf gekozen klant (vanaf de klantpagina) — sla de zoekstap over en laad
    // meteen de contactpersonen. Particulier: PersonContact wordt auto-gekoppeld.
    if (preset) {
      setPickedAccountId(preset.accountId);
      setPickedAccountName(preset.name);
      setPickedIsBusiness(preset.isBusiness);
      void fetchAccountContacts(preset.accountId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preset?.accountId]);

  // Klant-zoek (debounced) — alleen accounts, geen leads (een losse factuur
  // vereist een bestaand Account voor adres + e-mail).
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        const response = await fetch(
          `/api/customers/search?q=${encodeURIComponent(trimmed)}`,
          { headers, signal: controller.signal }
        );
        if (!response.ok) {
          setSearchResults([]);
          return;
        }
        const data = await response.json();
        const all = (data.results as CustomerSearchResult[]) || [];
        setSearchResults(all.filter((r) => r.type !== 'lead').slice(0, 8));
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  if (!isOpen) return null;

  async function fetchAccountContacts(accountId: string, autoPickDefault: boolean) {
    setLoadingContacts(true);
    setAccountContacts(null);
    setDefaultContactId(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
      const response = await fetch(
        `/api/customers/contacts-by-account?id=${encodeURIComponent(accountId)}`,
        { headers }
      );
      if (!response.ok) {
        setAccountContacts([]);
        return;
      }
      const data = (await response.json()) as {
        contacts?: ContactRow[];
        defaultContactId?: string | null;
      };
      setAccountContacts(data.contacts ?? []);
      setDefaultContactId(data.defaultContactId ?? null);
      // Koppel direct een contactpersoon (standaard, anders het eerste). De
      // factuur-mailflow rendert tegen een contact — zonder contactpersoon gaat
      // de automatische verzending stil mis.
      if (autoPickDefault) {
        const fallback = data.defaultContactId || data.contacts?.[0]?.id || null;
        if (fallback) setPickedContactId(fallback);
      }
    } catch {
      setAccountContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  function pickCustomer(r: CustomerSearchResult) {
    setSearchQuery('');
    setSearchResults([]);
    setPickedAccountId(r.id);
    setPickedAccountName(r.name);
    setPickedBlacklist(r.blacklisted ? { reden: r.blacklistReden ?? null } : null);
    setPickedContactId(null);
    setAccountContacts(null);
    setDefaultContactId(null);

    if (r.type === 'account_business') {
      setPickedIsBusiness(true);
      void fetchAccountContacts(r.id, true);
    } else {
      // PersonAccount: PersonContactId auto-koppelen (defaultContactId).
      setPickedIsBusiness(false);
      void fetchAccountContacts(r.id, true);
    }
  }

  function clearPickedCustomer() {
    setPickedAccountId(null);
    setPickedAccountName('');
    setPickedIsBusiness(false);
    setPickedBlacklist(null);
    setAccountContacts(null);
    setDefaultContactId(null);
    setPickedContactId(null);
  }

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const updateLine = (idx: number, patch: Partial<LineRow>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const exclTotal = lines.reduce((sum, l) => sum + lineExcl(l), 0);
  const btwTotal = lines.reduce(
    (sum, l) => sum + Math.round(lineExcl(l) * (l.btwPct / 100) * 100) / 100,
    0
  );
  const inclTotal = Math.round((exclTotal + btwTotal) * 100) / 100;

  const linesValid = lines.every(
    (l) => l.omschrijving.trim().length > 0 && parseNum(l.prijs) >= 0
  );
  const canSubmit =
    pickedAccountId !== null && linesValid && inclTotal > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !pickedAccountId) return;
    setSubmitting(true);
    setError(null);

    const payloadLines: StandaloneLineInput[] = lines.map((l) => ({
      omschrijving: l.omschrijving.trim(),
      aantal: Math.max(1, Math.round(parseNum(l.aantal)) || 1),
      prijsExclBtw: parseNum(l.prijs),
      btwPct: l.btwPct,
    }));

    const result = await createStandaloneInvoice({
      accountId: pickedAccountId,
      contactId: pickedContactId || undefined,
      lines: payloadLines,
      opmerkingen: opmerking.trim() || undefined,
      // Gekozen merk stuurt zowel de PDF als de gebrande e-mail.
      brand,
    });

    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onCreated(result.invoiceId);
  };

  const showContactPicker =
    pickedIsBusiness && (loadingContacts || (accountContacts !== null && accountContacts.length > 0));

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Nieuwe losse factuur</h2>
            <p className="text-xs text-slate-500 mt-1">
              Vrije regels — voor een verkoop buiten een werkorder om.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* Merk — stuurt de branding op de PDF én de e-mail */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">Merk</legend>
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'CINCO', label: 'Cinco Cleaning' },
                { value: 'KLEDA', label: 'Kleda' },
              ] as const).map((opt) => {
                const selected = brand === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBrand(opt.value)}
                    disabled={submitting}
                    aria-pressed={selected}
                    className={clsx(
                      'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      selected
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-500'
                        : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Bepaalt logo, afzender en footer op de factuur-PDF en -e-mail.
            </p>
          </fieldset>

          {/* 1. Klant */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">1. Klant</legend>
            {!pickedAccountId ? (
              <>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Zoek op naam, e-mail, telefoon of postcode…"
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {(searching || searchResults.length > 0) && (
                  <div className="mt-2 border border-slate-200 rounded-md bg-white max-h-56 overflow-y-auto">
                    {searching && <div className="px-3 py-2 text-xs text-slate-500">Zoeken…</div>}
                    {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                      <div className="px-3 py-2 text-xs text-slate-500">
                        Geen bestaande klant gevonden. Maak de klant eerst aan via Nieuwe aanvraag.
                      </div>
                    )}
                    {searchResults.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        onClick={() => pickCustomer(r)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-slate-900 flex items-center gap-1.5">
                            {r.name}
                            {r.blacklisted && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700">
                                ⛔ Blacklist
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide text-slate-500 flex-shrink-0">
                            {r.type === 'account_business' ? 'Zakelijk' : 'Particulier'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {[r.email, r.phone, r.city].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm">
                <span className="text-emerald-900 font-medium">{pickedAccountName}</span>
                <button
                  type="button"
                  onClick={clearPickedCustomer}
                  className="text-xs text-emerald-700 hover:underline"
                >
                  Andere klant
                </button>
              </div>
            )}

            {pickedBlacklist && (
              <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <span className="font-semibold">⛔ Deze klant staat op de blacklist.</span>
                {pickedBlacklist.reden && (
                  <span className="block mt-0.5 text-red-700">Reden: {pickedBlacklist.reden}</span>
                )}
              </div>
            )}

            {showContactPicker && (
              <div className="mt-2">
                <div className="text-[11px] font-medium text-slate-700 mb-1">
                  Contactpersoon (optioneel)
                </div>
                <div className="border border-slate-200 rounded-md bg-white max-h-44 overflow-y-auto">
                  {loadingContacts && (
                    <div className="px-3 py-2 text-xs text-slate-500">Contacten laden…</div>
                  )}
                  {!loadingContacts &&
                    accountContacts &&
                    accountContacts.map((c) => {
                      const isSelected = c.id === pickedContactId;
                      const isDefault = c.id === defaultContactId;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            setPickedContactId((prev) => (prev === c.id ? null : c.id))
                          }
                          className={clsx(
                            'w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0',
                            isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50'
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={clsx(
                                'font-medium',
                                isSelected ? 'text-indigo-900' : 'text-slate-900'
                              )}
                            >
                              {c.name}
                            </span>
                            <div className="flex items-center gap-1">
                              {isDefault && (
                                <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                  Standaard
                                </span>
                              )}
                              {isSelected && (
                                <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          {(c.email || c.title) && (
                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                              {[c.title, c.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </fieldset>

          {/* 2. Regels */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">2. Regels</legend>
            <div className="space-y-2">
              <div className="hidden sm:flex items-center gap-2 px-1 text-[11px] uppercase tracking-wide text-slate-400">
                <span className="flex-1">Omschrijving</span>
                <span className="w-14 text-right">Aantal</span>
                <span className="w-24 text-right">Prijs excl.</span>
                <span className="w-16">Btw</span>
                <span className="w-6" />
              </div>
              {lines.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={l.omschrijving}
                    onChange={(e) => updateLine(idx, { omschrijving: e.target.value })}
                    placeholder="bv. Vezelbeschermer 500ml"
                    disabled={submitting}
                    className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={l.aantal}
                    onChange={(e) => updateLine(idx, { aantal: e.target.value })}
                    disabled={submitting}
                    className="w-14 border border-slate-300 rounded px-2 py-1.5 text-sm text-right"
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={l.prijs}
                    onChange={(e) => updateLine(idx, { prijs: e.target.value })}
                    placeholder="€ excl."
                    disabled={submitting}
                    className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm text-right"
                  />
                  <select
                    value={l.btwPct}
                    onChange={(e) =>
                      updateLine(idx, { btwPct: Number(e.target.value) as 0 | 9 | 21 })
                    }
                    disabled={submitting}
                    className="w-16 border border-slate-300 rounded px-1.5 py-1.5 text-sm bg-white"
                  >
                    <option value={21}>21%</option>
                    <option value={9}>9%</option>
                    <option value={0}>0%</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    disabled={submitting || lines.length === 1}
                    className="w-6 text-slate-400 hover:text-red-600 disabled:opacity-30 text-sm"
                    aria-label="Regel verwijderen"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLine}
              disabled={submitting}
              className="mt-2 text-xs text-indigo-600 hover:underline"
            >
              + Regel toevoegen
            </button>
          </fieldset>

          {/* 3. Opmerking */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">
              3. Opmerking op factuur (optioneel)
            </legend>
            <textarea
              value={opmerking}
              onChange={(e) => setOpmerking(e.target.value)}
              rows={2}
              maxLength={2000}
              disabled={submitting}
              placeholder="Verschijnt op de factuur"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </fieldset>

          {/* Totaal */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-600">Subtotaal (excl. btw)</span>
              <span className="tabular-nums">{formatCurrency(exclTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Btw</span>
              <span className="tabular-nums">{formatCurrency(btwTotal)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1 font-medium">
              <span className="text-slate-700">Totaal incl. btw</span>
              <span className="tabular-nums">{formatCurrency(inclTotal)}</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400">
            Na aanmaken krijgt de factuur automatisch een Mollie-betaallink en wordt hij
            kort daarna per e-mail verstuurd. Controleer adres en e-mail op de factuurpagina.
          </p>

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Aanmaken…' : 'Factuur aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}
